import React, { useState, useEffect } from "react";
import notificationService from "../../redux/notification/notificationService";
import Header from "../../components/Header/Header";
import AssignRMTable from "../../components/AssignRMTable/AssignRMTable";
import api from "../../config/apiConfig";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch } from "react-redux";
import { getAllUserForRM, getUser } from "../../redux/user/userSlice";

interface User {
  id: number;
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  mobile: string;
  logInBy: number;
  onboardStatus: number;
  createdAt: string;
}

const AssignRM: React.FC = () => {
  const dispatch = useDispatch<any>();
  const userType = localStorage.getItem("userType");
  const [requests, setRequests] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [selectedData, setSelectedData] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [sortOption, setSortOption] = useState<number>(4);
  const [filterOption, setFilterOption] = useState<any>({
    selectedRM: null,
    isRM: false,
  });

  const fetchUsers = async (
    page: number,
    rowsPerPage: number,
    sortOption: number
  ) => {
    setLoading(true);
    try {
      const response = await dispatch(
        getUser({
          pageIndex: page, // Pass page as 1-based index
          pageSize: rowsPerPage,
          sortBy: sortOption,
        })
      );

      const data = response.payload.data;
      const totalPages = response.payload.totalPages;
      const currentPage = Math.min(response.payload.pageIndex, totalPages);

      setRequests(data);
      setTotalPages(totalPages);
      setError(null);
    } catch (error) {
      setError("Failed to fetch users");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRMUsers = async (
    page: number,
    rowsPerPage: number,
    sortOption: number
  ) => {
    setLoading(true);
    try {
      const response = await dispatch(
        getAllUserForRM({
          pageIndex: page,
          rmId: filterOption.selectedRM,
          pageSize: rowsPerPage,
          forRm: filterOption.isRM ? 1 : userType === "6" ? 0 : -1,
          sortBy: sortOption,
          onboardStatus: -1,
          startDate: "",
          endDate: "",
          search: "",
        })
      );
      const userData = response.payload.data || [];
      const totalPages = response.payload.totalPages;
      setRequests(userData);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Error fetching user data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filterOption.selectedRM) {
      fetchRMUsers(page, rowsPerPage, sortOption);
    } else {
      fetchUsers(page, rowsPerPage, sortOption);
    }
  }, [dispatch, page, sortOption, rowsPerPage, filterOption]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1); // Increment by 1 to align with the 1-based index
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to the first page when changing rows per page
  };

  const handleChangeStatus = async (
    userType: number,
    requestId: number,
    status: number
  ) => {
    try {
      setLoading(true);
      await api.post(
        "admin/rm/assign",
        {
          userType: userType,
          rmId: status,
          userId: [requestId],
        },
        {
          headers: {
            Authorization: localStorage.getItem("accessToken"),
          },
        }
      );
      fetchUsers(page, rowsPerPage, sortOption);
    } catch (error) {
      console.error("Failed to change status", error);
      toast.error("Failed to update Manager. Reloading the page.");
      // Reload the page if there's an error as well
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetails = async (id: number) => {
    try {
      console.log("Fetching details for ID:", id);
      const response = await notificationService.getNotificationById({
        id: id,
      });
      setSelectedData(response.data);
      setDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch notification by ID:", error);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedData(null);
  };

  return (
    <>
      <div className="requests">
        <AssignRMTable
          requests={requests}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          loading={loading}
          error={error}
          sortBy={sortOption}
          onChangeSortBy={setSortOption}
          onChangeStatus={handleChangeStatus}
          onOpenDetails={handleOpenDetails}
          filterOption={filterOption}
          setFilterOption={setFilterOption}
        />
        <Toaster />
      </div>
    </>
  );
};

export default AssignRM;
